import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { CITIES } from "@/lib/cities";

const KRAJ_ORDER = [
  "Bratislavský kraj", "Trnavský kraj", "Trenčiansky kraj", "Nitriansky kraj",
  "Žilinský kraj", "Banskobystrický kraj", "Prešovský kraj", "Košický kraj",
];

const CZ_KRAJ_ORDER = [
  "Hlavní město Praha", "Středočeský kraj", "Jihočeský kraj", "Plzeňský kraj",
  "Karlovarský kraj", "Ústecký kraj", "Liberecký kraj", "Královéhradecký kraj",
  "Pardubický kraj", "Kraj Vysočina", "Jihomoravský kraj", "Olomoucký kraj",
  "Zlínský kraj", "Moravskoslezský kraj",
];

// Krajské (regionálne) mestá — zvýraznené v rozcestníku
const KRAJSKE = [
  ["bratislava", "Bratislava"], ["trnava", "Trnava"], ["trencin", "Trenčín"],
  ["nitra", "Nitra"], ["zilina", "Žilina"], ["banska-bystrica", "Banská Bystrica"],
  ["presov", "Prešov"], ["kosice", "Košice"],
];

// Populárne SEO témy — interné prelinkovanie na kľúčové vstupné stránky
const THEMES = [
  ["Zateplenie rodinného domu", "/zateplenie-rodinneho-domu/bratislava"],
  ["Zateplenie bytového domu", "/zateplenie-bytoveho-domu/bratislava"],
  ["Cena zateplenia za m²", "/zateplenie-cena-za-m2/bratislava"],
  ["Zateplenie polystyrénom (EPS)", "/zateplenie-polystyrenom/bratislava"],
  ["Zateplenie minerálnou vatou", "/zateplenie-mineralnou-vatou/bratislava"],
  ["Dotácia na zateplenie", "/dotacia-na-zateplenie/bratislava"],
  ["Porovnanie materiálov", "/materialy"],
  ["Fasádne omietky", "/omietky"],
  ["Časté otázky (FAQ)", "/faq"],
  ["Blog a návody", "/blog"],
];

export default function Cities() {
  const [towns, setTowns] = useState([]);
  const [openKraj, setOpenKraj] = useState(null);
  const [openKrajCz, setOpenKrajCz] = useState(null);

  useEffect(() => {
    api.get("/seo/towns")
      .then((r) => setTowns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTowns([]));
  }, []);

  const skTowns = towns.filter((t) => (t.country || "SK") === "SK");
  const czTowns = towns.filter((t) => t.country === "CZ");

  const byKraj = KRAJ_ORDER.map((kraj) => ({
    kraj,
    towns: skTowns
      .filter((t) => t.region === kraj)
      .sort((a, b) => a.name.localeCompare(b.name, "sk")),
  })).filter((g) => g.towns.length > 0);

  const byKrajCz = CZ_KRAJ_ORDER.map((kraj) => ({
    kraj,
    towns: czTowns
      .filter((t) => t.region === kraj)
      .sort((a, b) => a.name.localeCompare(b.name, "cs")),
  })).filter((g) => g.towns.length > 0);

  return (
    <section id="mesta" className="bg-ink-2 py-24 border-t border-white/10" data-testid="seo-hub">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-12">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Zatepľujeme po celom Slovensku aj v Česku</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] text-white">
            Zateplenie domu vo<br /><span className="text-gold">vašom meste</span>
          </h2>
        </div>

        {/* Populárne témy */}
        <div className="mb-14">
          <p className="font-mono2 text-white/40 uppercase tracking-widest text-xs mb-4">Populárne témy</p>
          <div className="flex flex-wrap gap-3">
            {THEMES.map(([label, href]) => (
              <Link
                key={href}
                to={href}
                data-testid={`hub-theme-${href.replace(/\//g, "-")}`}
                className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>

        {/* Krajské mestá */}
        <p className="font-mono2 text-white/40 uppercase tracking-widest text-xs mb-4">Krajské mestá</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-14">
          {(towns.length ? KRAJSKE : CITIES.map((c) => [c.slug, c.name])).map(([slug, name]) => (
            <Link
              key={slug}
              to={`/zateplenie/${slug}`}
              data-testid={`home-city-${slug}`}
              className="group flex items-center justify-between p-5 border border-white/10 bg-ink hover:border-gold hover:bg-gold/5 transition-colors"
            >
              <span className="text-white group-hover:text-gold transition-colors font-medium">{name}</span>
              <MapPin className="w-4 h-4 text-white/30 group-hover:text-gold transition-colors" />
            </Link>
          ))}
        </div>

        {/* Všetky mestá podľa kraja — rozbaľovacie */}
        {byKraj.length > 0 && (
          <div>
            <p className="font-mono2 text-white/40 uppercase tracking-widest text-xs mb-4">
              Všetky mestá podľa kraja ({skTowns.length})
            </p>
            <div className="space-y-2">
              {byKraj.map((g) => {
                const open = openKraj === g.kraj;
                return (
                  <div key={g.kraj} className="border border-white/10 bg-ink">
                    <button
                      onClick={() => setOpenKraj(open ? null : g.kraj)}
                      data-testid={`hub-kraj-${g.kraj}`}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
                    >
                      <span className="text-white font-medium">{g.kraj} <span className="text-white/35 text-sm">· {g.towns.length} miest</span></span>
                      <ChevronDown className={`w-5 h-5 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="px-5 pb-5 pt-1 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/5">
                        {g.towns.map((t) => (
                          <Link
                            key={t.slug}
                            to={`/zateplenie/${t.slug}`}
                            className="text-white/60 hover:text-gold transition-colors text-sm"
                          >
                            Zateplenie {t.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Česko — GEO pokrytie ČR */}
        {byKrajCz.length > 0 && (
          <div className="mt-16 pt-14 border-t border-white/10" data-testid="seo-hub-cz">
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-4">Zateplujeme i v Česku</p>
            <p className="font-mono2 text-white/40 uppercase tracking-widest text-xs mb-4">
              Města podle kraje ({czTowns.length})
            </p>
            <div className="space-y-2">
              {byKrajCz.map((g) => {
                const open = openKrajCz === g.kraj;
                return (
                  <div key={g.kraj} className="border border-white/10 bg-ink">
                    <button
                      onClick={() => setOpenKrajCz(open ? null : g.kraj)}
                      data-testid={`hub-kraj-cz-${g.kraj}`}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
                    >
                      <span className="text-white font-medium">{g.kraj} <span className="text-white/35 text-sm">· {g.towns.length} měst</span></span>
                      <ChevronDown className={`w-5 h-5 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="px-5 pb-5 pt-1 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/5">
                        {g.towns.map((t) => (
                          <Link
                            key={t.slug}
                            to={`/zatepleni/${t.slug}`}
                            className="text-white/60 hover:text-gold transition-colors text-sm"
                          >
                            Zateplení {t.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
